// The words the generator wrote for each card are put on the picture for the host, as a text
// layer they can move or delete in Text. Which look a shape gets, where the words sit, and when a
// post needs it. Pure rules, no React, no network.
import type { PostDto, VersionDto } from '@str/api/types';
import { CANVAS_SIZE, type Canvas, type TextLayer } from '@str/shared';

/** How the words are set: after the shape the brief starts with. */
export type TextLook = 'quote' | 'spotlight' | 'offer' | 'plain' | 'none';

export const WORDS_NOTE = 'Words on the picture';

/** Which third of the picture the words sit in. */
export type Band = 'top' | 'bottom';

/**
 * Where the words make sense: the band with less going on in it, measured as the detail in the
 * top third against the bottom third of the picture (see picture-bands). A tie keeps them low,
 * where words on a picture usually sit.
 */
export function calmerBand(samples: { top: number; bottom: number }): Band {
  return samples.top < samples.bottom ? 'top' : 'bottom';
}

/**
 * The review the wizard put in a guest-quote brief, word for word with its author on a second
 * line; null when the brief carries none.
 */
export function quoteFromBrief(brief: string): string | null {
  const m = brief.match(
    /Quote this review word for word:\s*[“"]([^”"]+)[”"](?:\s*[—-]\s*([^.\n]+?))?(?=[.\n]|$)/,
  );
  if (!m) return null;
  const quote = m[1]!.trim();
  const by = m[2]?.trim();
  return by ? `“${quote}”\n— ${by}` : `“${quote}”`;
}

/**
 * The words for one card. A guest quote is the review itself, on the first card only: the
 * generator's card headlines name what each photo shows, which is not what a quote post says.
 * Every other look writes each card's own headline.
 */
export function wordsFor(
  brief: string,
  cards: readonly { headline: string; words?: string | null }[],
  i: number,
): string {
  const look = lookFor(brief);
  if (look === 'none') return '';
  // The words the host asked for: the picture was made without them, so they go on here.
  const asked = cards[i]?.words?.trim();
  if (asked) return asked;
  if (look !== 'quote') return cards[i]?.headline.trim() ?? '';
  if (i !== 0) return '';
  return quoteFromBrief(brief) ?? cards[0]?.headline.trim() ?? '';
}

/** The shape is the first words of the wizard brief; anything else gets the plain look. */
export function lookFor(brief: string): TextLook {
  const head = brief.trim().toLowerCase().slice(0, 60);
  if (head.startsWith('a guest review')) return 'quote';
  if (head.startsWith('an amenity spotlight')) return 'spotlight';
  if (head.startsWith('promote open dates') || head.startsWith('a last-minute opening'))
    return 'offer';
  if (head.startsWith('the photos as they are with nothing written')) return 'none';
  return 'plain';
}

type Look = {
  font: TextLayer['font'];
  align: TextLayer['align'];
  size: number;
  x: number;
  w: number;
  /** Where the block's bottom edge sits, as a fraction of the height; top-anchored when `top`. */
  anchor: number;
  top?: boolean;
  pill: string | null;
  shade: boolean;
};

const LOOKS: Record<Exclude<TextLook, 'none'>, Look> = {
  quote: {
    font: 'serif',
    align: 'center',
    size: 0.066,
    x: 0.08,
    w: 0.84,
    anchor: 0.9,
    pill: null,
    shade: false,
  },
  spotlight: {
    font: 'sans',
    align: 'left',
    size: 0.11,
    x: 0.08,
    w: 0.84,
    anchor: 0.9,
    pill: null,
    shade: false,
  },
  offer: {
    font: 'sans',
    align: 'center',
    size: 0.058,
    x: 0.12,
    w: 0.76,
    anchor: 0.08,
    top: true,
    pill: null,
    shade: false,
  },
  plain: {
    font: 'sans',
    align: 'left',
    size: 0.078,
    x: 0.08,
    w: 0.84,
    anchor: 0.9,
    pill: null,
    shade: false,
  },
};

/** A rough measure of the block the renderer will draw: lines wrapped at the width, 1.05 leading. */
export function fitWords(
  text: string,
  size: number,
  w: number,
  canvas: Canvas,
): { lines: number; height: number } {
  const dims = CANVAS_SIZE[canvas];
  const perLine = Math.max(4, Math.floor(w / (size * 0.52)));
  let lines = 0;
  for (const para of text.split('\n')) {
    const words = para.split(/\s+/).filter(Boolean);
    let len = 0;
    let n = 1;
    for (const word of words) {
      const add = (len ? 1 : 0) + word.length;
      if (len + add > perLine && len) {
        n += 1;
        len = word.length;
      } else len += add;
    }
    lines += n;
  }
  const height = (lines * size * 1.05 * dims.width) / dims.height;
  return { lines, height };
}

const MIN_SIZE = 0.035;
/** The block's edge when it sits in the top band, and when it sits in the bottom one. */
const TOP_ANCHOR = 0.08;
const BOTTOM_ANCHOR = 0.9;

/**
 * The layer for one card's words: sized to fit, kept inside the picture, in the shape's look.
 * The look says which band the words sit in; `band` moves them to the other one when the
 * picture is calmer there, and the font, size, alignment and shade stay the look's own.
 */
export function wordsLayer(
  headline: string,
  look: Exclude<TextLook, 'none'>,
  canvas: Canvas,
  id: string,
  { band }: { band?: Band } = {},
): TextLayer {
  const base = LOOKS[look];
  const top = band ? band === 'top' : !!base.top;
  const L: Look = { ...base, top, anchor: top ? TOP_ANCHOR : BOTTOM_ANCHOR };
  const text = headline.trim().slice(0, 300);
  // Long words shrink, gently, so a whole review still fits in the lower part of the picture.
  let size = Math.max(
    MIN_SIZE,
    Math.min(L.size, L.size * Math.sqrt(48 / Math.max(1, text.length))),
  );
  let fit = fitWords(text, size, L.w, canvas);
  const room = L.top ? 0.95 - L.anchor : L.anchor - 0.05;
  while (fit.height > room && size > MIN_SIZE) {
    size = Math.max(MIN_SIZE, size * 0.9);
    fit = fitWords(text, size, L.w, canvas);
  }
  const y = L.top ? L.anchor : Math.max(0.05, L.anchor - fit.height);
  return {
    id,
    kind: 'text',
    text,
    x: L.x,
    y: Math.min(y, 0.95 - fit.height),
    w: L.w,
    rotate: 0,
    size: Number(size.toFixed(4)),
    font: L.font,
    color: '#ffffff',
    align: L.align,
    pill: L.pill,
    shade: L.shade,
  };
}

/**
 * The cards of an AI version whose words still need putting on: original photos (a card the
 * generator made from the host's own photo, or fell back to after a drift) with a headline and
 * nothing drawn on them yet, and any card carrying words the host asked for (the generator made
 * that picture without lettering and handed the words over). Other generated artwork may carry
 * lettering of its own, so it never gets a second headline. A card with no origin of its own
 * takes the post's mode: photos means the host's photo, anything else means generated.
 */
export function cardsNeedingWords(
  brief: string,
  v: VersionDto,
  mediaMode: PostDto['mediaMode'] = 'photos',
): number[] {
  if (lookFor(brief) === 'none') return [];
  const out: number[] = [];
  for (const [i, c] of v.cards.entries()) {
    if (c.edit) continue;
    if (c.words?.trim()) {
      out.push(i);
      continue;
    }
    const origin = c.origin ?? (mediaMode === 'photos' ? 'host' : 'ai');
    if (origin !== 'host' || !c.headline.trim()) continue;
    out.push(i);
  }
  return out;
}

/**
 * The version whose words still need putting on: the current one, made by the AI, with at least
 * one original-photo card that has a headline, no explicit edit drawn on any card, and no host
 * version built on it yet. Explicit host layers, including an empty layer list, record the
 * host’s visual intent and are left alone.
 */
export function needsWords(post: PostDto): VersionDto | null {
  if (post.format === 'Reel' || post.format === 'Story') return null;
  if (lookFor(post.brief) === 'none') return null;
  const v = post.versions.find((x) => x.id === post.currentVersionId);
  if (!v || v.origin !== 'ai' || v.videoUrl) return null;
  if (v.cards.some((c) => c.edit)) return null;
  if (cardsNeedingWords(post.brief, v, post.mediaMode).length === 0) return null;
  if (post.versions.some((x) => x.parentVersionId === v.id)) return null;
  return v;
}
